/**
 * The one-glyph answer to "has this site been through the brain yet".
 *
 * Without it the triage view is blind. An operator scanning a page of leads
 * cannot tell which companies already have a run, so the expensive button gets
 * pressed twice on the same site and never on the rest of the list. The badge
 * is the cheapest possible fix: one LEFT JOIN on the domain the two tables
 * already share, one glyph per row.
 *
 * Deliberately not a button. Everything clickable in a lead row has to be
 * lifted above the row's stretched overlay, and a second click target inside a
 * 30px track is a mis-click waiting to happen - the row menu is where the
 * action lives.
 */
import { Brain, Loader2 } from "lucide-react";
import type { BrainBadge } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

export function BrainBadgeCell({ brain, t }: { brain: BrainBadge | null; t: T }) {
  if (!brain) {
    // An empty cell rather than a grey glyph: "never run" is the state of most
    // rows, and marking every one of them would be noise, not information.
    return <span aria-hidden />;
  }

  if (brain.status === "running") {
    return (
      <span title={t("brain.badge.running")} className="text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" />
      </span>
    );
  }

  const failed = brain.status === "error";
  const title = failed
    ? t("brain.badge.failed")
    : [
        t("brain.badge.done", { count: brain.findings, date: fmtDay(brain.finishedAt) }),
        // The verdict rides in the tooltip rather than as a second glyph. A row
        // this narrow cannot carry two marks, and the colour below already
        // carries the part that has to be readable at a glance.
        brain.fit ? t(`brain.fit.decision.${brain.fit}`) : null,
      ]
        .filter(Boolean)
        .join(" - ");

  return (
    <span
      className="inline-flex items-center gap-0.5 text-xs tabular-nums"
      style={{ color: failed ? "var(--color-muted-foreground)" : FIT_COLOUR[brain.fit ?? "none"] }}
      title={title}
    >
      <Brain className="size-3.5" />
      {failed ? null : brain.findings}
    </span>
  );
}

/**
 * The glyph's colour is the verdict.
 *
 * A company the brain already said no to must be skippable without opening
 * anything, which is the entire reason the fit is carried on the badge at all.
 * "No verdict yet" stays the neutral primary rather than borrowing a warning
 * colour - not thought about is not the same as thought about and rejected.
 */
const FIT_COLOUR: Record<"go" | "hold" | "no" | "none", string> = {
  go: "var(--color-success)",
  hold: "var(--color-warning)",
  no: "var(--color-muted-foreground)",
  none: "var(--color-primary)",
};

/** Date only. A findings run is compared in days, never in minutes. */
function fmtDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}
